import React from "react";
import BranchSelect from "../../../components/ui/BranchSelect";
import EmployeePicker from "../../../components/ui/EmployeePicker";
import { useDepartments } from "../../../api/queries/organizationQueries";
import { useLeaveTypes } from "../../../api/queries/leaveQueries";
import { useAssetTypes } from "../../../api/queries/assetQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";

const EMPTY = [];

/** Import modes, explained. */
export const MODE_LABEL = {
  CREATE: "Add new only",
  UPDATE: "Update existing only",
  UPSERT: "Add new and update existing",
};
export const MODE_HELP = {
  CREATE:
    "Add new records only. Rows that match an existing record are skipped.",
  UPDATE:
    "Change records that already exist (found by the key below). Rows that match nothing are skipped.",
  UPSERT: "Update a record when it already exists, otherwise add it.",
};

const DepartmentSelect = ({ value, onChange, id }) => {
  const user = useCurrentUser();
  const { data = EMPTY } = useDepartments(user.companyId);
  return (
    <select
      id={id}
      className="form-select"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Any department</option>
      {data.map((d) => (
        <option key={d.id} value={d.id}>
          {d.departmentName}
        </option>
      ))}
    </select>
  );
};
const LeaveTypeSelect = ({ value, onChange, id }) => {
  const { data = EMPTY } = useLeaveTypes();
  return (
    <select
      id={id}
      className="form-select"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Any leave type</option>
      {data.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
};
const AssetTypeSelect = ({ value, onChange, id }) => {
  const { data = EMPTY } = useAssetTypes(true);
  return (
    <select
      id={id}
      className="form-select"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Any asset type</option>
      {data.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
};
const EmployeeFilter = ({ value, onChange, id }) => {
  const user = useCurrentUser();
  const [picked, setPicked] = React.useState(null);
  return (
    <EmployeePicker
      id={id}
      companyId={user.companyId}
      value={value ? picked : null}
      onChange={(emp) => {
        setPicked(emp);
        onChange(emp?.id || "");
      }}
      placeholder="Any employee"
    />
  );
};

/**
 * One export filter, generated from the dataset definition:
 * date / enum / boolean / string inputs; uuid filters get a picker by key name.
 * Values are strings (the API takes { key: string }).
 */
export const FilterField = ({ filter, value, onChange }) => {
  const id = `filter-${filter.key}`;
  const set = (v) =>
    onChange(filter.key, v === null || v === undefined ? "" : String(v));
  let input;
  if (filter.type === "date") {
    input = (
      <input
        id={id}
        type="date"
        className="form-control"
        value={value || ""}
        onChange={(e) => set(e.target.value)}
      />
    );
  } else if (filter.type === "enum") {
    input = (
      <select
        id={id}
        className="form-select"
        value={value || ""}
        onChange={(e) => set(e.target.value)}
      >
        <option value="">Any</option>
        {(filter.enumValues || []).map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    );
  } else if (filter.type === "boolean") {
    input = (
      <select
        id={id}
        className="form-select"
        value={value || ""}
        onChange={(e) => set(e.target.value)}
      >
        <option value="">Any</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  } else if (filter.type === "uuid") {
    const key = filter.key.toLowerCase();
    if (key.includes("branch"))
      input = <BranchSelect id={id} value={value} onChange={(v) => set(v)} />;
    else if (key.includes("department"))
      input = <DepartmentSelect id={id} value={value} onChange={set} />;
    else if (key.includes("leavetype"))
      input = <LeaveTypeSelect id={id} value={value} onChange={set} />;
    else if (key.includes("assettype"))
      input = <AssetTypeSelect id={id} value={value} onChange={set} />;
    else if (key.includes("employee"))
      input = <EmployeeFilter id={id} value={value} onChange={set} />;
    else
      input = (
        <input
          id={id}
          className="form-control font-monospace"
          value={value || ""}
          onChange={(e) => set(e.target.value.trim())}
          placeholder="ID"
        />
      );
  } else {
    input = (
      <input
        id={id}
        className="form-control"
        value={value || ""}
        onChange={(e) => set(e.target.value)}
      />
    );
  }
  return (
    <div>
      <label htmlFor={id} className="profile-label">
        {filter.label}
      </label>
      {input}
      {filter.help && (
        <div className="fs-070 text-slate-500 mt-1">{filter.help}</div>
      )}
    </div>
  );
};

/** Saved templates as quick picks. */
export const TemplatePicks = ({
  templates = [],
  activeId,
  onPick,
  onDelete,
  label = "Saved:",
}) =>
  templates.length === 0 ? null : (
    <div className="d-flex flex-wrap gap-2 align-items-center">
      <span className="fs-075 text-slate-500">{label}</span>
      {templates.map((t) => (
        <span
          key={t.id}
          className={`chip ${activeId === t.id ? "" : "chip--alt"}`}
          title={t.description || ""}
        >
          <button
            type="button"
            className="chip-remove p-0 me-1 text-reset"
            onClick={() => onPick(t)}
            aria-label={`Use template ${t.name}`}
          >
            {t.name}
          </button>
          {onDelete && (
            <button
              type="button"
              className="chip-remove"
              onClick={() => onDelete(t)}
              aria-label={`Delete template ${t.name}`}
            >
              <i className="bi bi-x"></i>
            </button>
          )}
        </span>
      ))}
    </div>
  );
